import { useTranslation } from "react-i18next";
// MagicConsume — landing page for the link from the WYNGX login email.
// URL: /auth/magic/:token  → calls /auth/magic/consume → stores JWT →
// redirects to /dashboard.
//
// The actual cold-launch hang on iOS Capacitor was fixed at the network
// layer (see AuthContext.consumeMagicLink) by switching to a CORS-safe
// text/plain POST that skips the OPTIONS preflight. This component is
// now a thin, fast UI shell — no escape hatches needed.
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { diagLog } from "@/lib/diagnostics";

// Module-level guard: iOS Universal Links can fire BOTH getLaunchUrl()
// AND appUrlOpen for the same URL on cold-launch. Without this, a
// successful first consume would 401 on the second mount's POST.
const attempted = new Set();
export default function MagicConsume() {
  const {
    t
  } = useTranslation();
  const {
    token
  } = useParams();
  const nav = useNavigate();
  const {
    consumeMagicLink
  } = useAuth();
  const [error, setError] = useState(null);
  const consumed = useRef(false);

  // NB: We deliberately do NOT short-circuit if a cached `user` exists.
  // The magic link MUST be consumed — it might be for a different
  // account, or the cached session may be stale. Skipping the consume
  // here previously caused a premature nav("/dashboard") that hid
  // backend errors behind a Dashboard render crash → black screen.

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;
    if (token && attempted.has(token)) {
      nav("/dashboard", {
        replace: true
      });
      return;
    }
    if (token) attempted.add(token);
    (async () => {
      if (!token) {
        setError("Geen token in URL.");
        return;
      }
      diagLog("info", "magic", `consume start, token.len=${token.length}`);
      try {
        const u = await consumeMagicLink(token);
        diagLog("info", "magic", `consume OK email=${u?.email || "?"} onboarded=${u?.onboarded}`);
        toast.success(`Welkom${u?.name ? `, ${u.name}` : ""}!`);
        diagLog("info", "magic", "about to nav(/dashboard)");
        // Honour a sessionStorage `wyng_after_login` URL set by
        // landing pages (e.g. /gift/:token) so users bounce back
        // straight to the page they were trying to act on.
        let next = "/dashboard";
        try {
          const stash = sessionStorage.getItem("wyng_after_login");
          if (stash && stash.startsWith("/")) {
            next = stash;
            sessionStorage.removeItem("wyng_after_login");
          }
        } catch {}
        nav(next, {
          replace: true
        });
        // Belt-and-braces fallback: if React Router's nav() didn't
        // unmount us within 1.5 s, force it again via the router
        // (NOT window.location, which breaks the SPA context in
        // Capacitor — capacitor://localhost has no /dashboard file
        // and a hard reload re-runs the whole boot sequence).
        setTimeout(() => {
          if (window.location.pathname.startsWith("/auth/magic")) {
            diagLog("warn", "magic", "nav stuck on /auth/magic — retry via router");
            try {
              nav(next, {
                replace: true
              });
            } catch (e) {
              diagLog("error", "magic", `retry nav threw: ${e?.message || e}`);
            }
          }
        }, 1500);
      } catch (e) {
        const msg = e?.response?.data?.detail || (e?.name === "AbortError" ? "De verbinding duurde te lang. Vraag een nieuwe link aan." : "Link is ongeldig of verlopen.");
        diagLog("error", "magic", `consume FAIL: ${msg}`, `name=${e?.name} status=${e?.response?.status} stack=${e?.stack || ""}`);
        setError(msg);
      }
    })();
  }, [token, consumeMagicLink, nav]);
  return <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6 bg-background" data-testid="magic-consume-page">
            <div className="text-center max-w-xs">
                {!error && <>
                        <div className="h-14 w-14 mx-auto rounded-full bg-[#FFD60A]/15 border border-[#FFD60A]/30 flex items-center justify-center mb-4">
                            <Sparkles size={22} className="text-[#FFD60A]" />
                        </div>
                        <h1 className="font-display text-2xl font-bold mb-2">{t("magicConsume.inloggen")}</h1>
                        <p className="text-[13px] text-muted-foreground">
                            {t("magicConsume.een_tel_we_zetten_je_sessie_klaar")}
                        </p>
                        <Loader2 className="animate-spin text-[#FFD60A] mx-auto mt-5" />
                    </>}
                {error && <>
                        <div className="h-14 w-14 mx-auto rounded-full bg-destructive/15 border border-destructive/30 flex items-center justify-center mb-4">
                            <AlertCircle size={22} className="text-destructive" />
                        </div>
                        <h1 className="font-display text-2xl font-bold mb-2">{t("magicConsume.oeps")}</h1>
                        <p className="text-[13px] text-muted-foreground mb-5">{error}</p>
                        <button onClick={() => nav("/login", {
          replace: true
        })} data-testid="magic-back-login" className="rounded-full px-5 py-2.5 bg-[#FFD60A] text-black font-semibold text-[13px]">
                            {t("magicConsume.vraag_een_nieuwe_link")}
                        </button>
                    </>}
            </div>
        </div>;
}